// Whether the person is at the page: no pointer move, press, key, wheel, scroll or touch in this tab for
// a while (a tab shown again counts as someone back). One set of listeners for the whole tab, shared
// by every caller; checked once a minute, so a pointer moving costs a timestamp only.
//
// The input that ends idle on the notice (`IdleNotice`'s layer or the header's pill) only resumes: the
// press, and the mouseup and click it makes, never reach what is under it; a key does not reach the
// cell or field it was typed in, unless that is in a dialog (`swallowsWake`).

import { useCallback, useSyncExternalStore } from "react";
import { useBuildInfo } from "@axny/platform-ui";
import { IDLE_MS_KEY, idleMsFor, swallowsWake } from "./live-updates";

/** Marks the notice's layer and the header's pill: a press there only resumes. */
export const IDLE_NOTICE_ATTR = "data-idle-notice";
const ON_NOTICE = `[${IDLE_NOTICE_ATTR}]`;
const DIALOG = "[role=dialog],[role=alertdialog]";

const PRESSES = new Set(["pointerdown"]);
/** What a press goes on to make: kept from the page too, after a press that only resumed. */
const FOLLOWS = new Set(["mousedown", "touchstart", "pointerup", "mouseup", "touchend", "click"]);
const KEYS = new Set(["keydown"]);
const MOVES = ["pointermove", "wheel", "scroll"] as const;
const FOLLOW_MS = 1000;
const CHECK_MS = 60_000;

/** The test-only idle time (`live-updates.ts`): read once, when the page loads. */
const storedIdleMs = (() => {
  try {
    return window.localStorage.getItem(IDLE_MS_KEY);
  } catch {
    return null;
  }
})();

let lastInput = Date.now();
let idleAfter = Infinity;
let idle = false;
let keepFollowsUntil = 0;
const listeners = new Set<() => void>();
const say = () => {
  for (const l of listeners) l();
};

/** Someone is here: idle ends (the notice's button and pill call it too). */
export function wake() {
  lastInput = Date.now();
  if (idle) {
    idle = false;
    say();
  }
}

const keep = (e: Event) => {
  e.preventDefault();
  e.stopPropagation();
};

const onInput = (e: Event) => {
  if (FOLLOWS.has(e.type)) {
    if (Date.now() < keepFollowsUntil) {
      keep(e);
      if (e.type === "click") keepFollowsUntil = 0;
      return wake();
    }
  } else if (idle && (PRESSES.has(e.type) || KEYS.has(e.type))) {
    const press = PRESSES.has(e.type);
    const at = press ? e.target : document.activeElement;
    const el = at instanceof Element ? at : null;
    if (swallowsWake(press ? "press" : "key", !!el?.closest(ON_NOTICE), !!el?.closest(DIALOG))) {
      keep(e);
      if (press) keepFollowsUntil = Date.now() + FOLLOW_MS;
    }
  }
  wake();
};
// A layer appearing under a still pointer is no one moving it (some browsers send a move of 0).
const onMove = (e: Event) => {
  if (e instanceof PointerEvent && e.movementX === 0 && e.movementY === 0) return;
  wake();
};
const onShown = () => {
  if (document.visibilityState === "visible") wake();
};
const check = () => {
  const now = Date.now() - lastInput >= idleAfter;
  if (now !== idle) {
    idle = now;
    say();
  }
};

const PRESS_TYPES = [...PRESSES, ...FOLLOWS, ...KEYS];
let timer: ReturnType<typeof setInterval> | undefined;
function subscribe(listener: () => void, ms: number) {
  idleAfter = ms;
  if (!listeners.size) {
    lastInput = Date.now();
    idle = false;
    // Not passive: a press or key that only resumes is kept from the page (`preventDefault`).
    for (const t of PRESS_TYPES) window.addEventListener(t, onInput, { capture: true });
    for (const t of MOVES) window.addEventListener(t, onMove, { capture: true, passive: true });
    document.addEventListener("visibilitychange", onShown);
  }
  // Checked a tenth as often as the idle time (once a minute for 30 minutes; a short test time sooner).
  clearInterval(timer);
  timer = setInterval(check, Math.min(CHECK_MS, Math.max(1000, idleAfter / 10)));
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size) return;
    for (const t of PRESS_TYPES) window.removeEventListener(t, onInput, { capture: true });
    for (const t of MOVES) window.removeEventListener(t, onMove, { capture: true });
    document.removeEventListener("visibilitychange", onShown);
    clearInterval(timer);
  };
}

/** True once nobody has used this tab for `ms`; false again at the first input. */
export function useUserIdle(ms: number): boolean {
  const sub = useCallback((listener: () => void) => subscribe(listener, ms), [ms]);
  return useSyncExternalStore(sub, () => idle);
}

/** The routing pages' idle: 30 minutes (on staging and locally, the test-only `routing.idleMs` if set). */
export function useRoutingIdle(): boolean {
  return useUserIdle(idleMsFor(useBuildInfo().data?.appEnv, storedIdleMs));
}
